import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';
import { FocusableControlValueAccessor } from '../FocusableControlValueAccessor';
import { SwitchSize } from './switch-interface';

@Component({
  selector: 'opac-switch',
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
})
export class SwitchComponent implements FocusableControlValueAccessor {

  @HostBinding('class') get classAttribute() {
    return {
      small: this.size === 'small',
      medium: this.size === 'medium',
    };
  }

  /**
   * Sets boolean checked state
   */
  @Input('checked') set isChecked(value: boolean) {
    this.writeValue(value);
  }
  /**
   * Sets boolean disabled state
   */
  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }
  /**
   * Determines if boolean is invalid.
   */
  @Input() invalid: boolean = false;
  /**
   * Determines hidden input name to work with forms
   */
  @Input() name: string = '';
  /**
   * Sets required state for boolean.
   * This prop won't validate, it is just for accessibility reasons.
   */
  @Input() required: boolean = false;
  /**
   * Determines dialog size
   */
  @Input() size: SwitchSize = 'medium';
  /**
   * Determines aria label
   */
  @Input() ariaLabel: string = '';
  /**
   * Sets aria labelled by attribute
   */
  @Input() ariaLabelledBy: string | undefined = undefined;
  /**
   * Emits when element loose focus
   */
  @Output() blur = new EventEmitter<Event>();
  /**
   * Emits when checked property changes value
   */
  @Output() change = new EventEmitter<boolean>();

  @ViewChild('switchEl') switchEl: ElementRef<HTMLElement>;

  checked = false;
  isDisabled = false;

  onChange: (value: boolean) => void = noop;
  onTouch: () => void = noop;

  handleOnTouch(event: Event) {
    this.onTouch();
    this.blur.emit(event);
  }

  focus() {
    this.switchEl.nativeElement.focus();
  }

  toggleSwitch() {
    if (this.isDisabled) {
      return;
    }

    this.checked = !this.checked;
    this.change.emit(this.checked);
    this.onChange(this.checked);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(obj: boolean): void {
    this.checked = obj;
  }
}
